import React, { useState, useEffect } from "react";
import ProviderCard from "../cards/ProviderCard";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import tooltipRegistry from "../tooltips/tooltipRegistry";
import ResourceViewerModal from "../modals/ResourceViewerModal";
import CareTeamIcon from "../assets/care-team-1.png";
import PractitionerIconMale from "../assets/Doctor_Male_96.png";
import PractitionerIconFemale from "../assets/Doctor_Female_96.png";
import { Modal, Form, Button, ListGroup, Spinner, InputGroup, Row, Col, Accordion } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import {
    searchProvidersByTerm,
    fetchOrganizationsPractitioners,
    fetchRelatedPersonPractitioners,
    fetchCommunicationSenders,
    getCareTeamsForPatient,
    getCareTeam,
    fetchProviderByReference,
    fetchProvider
} from "../services/providerService";
import { resolveName } from "../utils/messageUtils";
import { useStore } from "react-redux";


const DEFAULT_SEARCH_TYPES = {
    providers: true,
    organizations: false,
    careTeams: false,
    attachmentRegistries: false,
    communications: false
};

const CATEGORY_LABELS = {
    providers: "Providers",
    organizations: "Organizations",
    careTeams: "Care Teams",
    attachmentRegistries: "Attachment Registries",
    communications: "Communications"
};

function ProviderSearch({ fhirClient, patientId, onRecipientsChange, recipients = [] }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState({
        providers: [],
        organizations: [],
        careTeams: [],
        attachmentRegistries: [],
        communications: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchTypes, setSearchTypes] = useState(DEFAULT_SEARCH_TYPES);
    const [searchMode, setSearchMode] = useState("provider");
    const [activeAccordionKeys, setActiveAccordionKeys] = useState([]);
    const [selectedForView, setSelectedForView] = useState(null);
    const [showFhirViewerModal, setShowFhirViewerModal] = useState(false);
    const [fhirViewerResource, setFhirViewerResource] = useState(null);
    const store = useStore();
    const [errorModalShow, setErrorModalShow] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState("");
    const [careTeamPractitionersMap, setCareTeamPractitionersMap] = useState({});

    // Fetch practitioners for careTeams
    useEffect(() => {
        if (results.careTeams?.length > 0) {
            const fetchAllPractitioners = async () => {
                const map = {};
                for (const resource of results.careTeams) {
                    if (!resource.reference) continue;
                    try {
                        const practitioner = await fetchProviderByReference(
                            resource.reference,
                            fhirClient,
                            store
                        );
                        if (practitioner) {
                            map[`${resource.reference}-${resource.careTeamId}`] = practitioner;
                        }
                    } catch (err) {
                        console.error("Failed to fetch practitioner:", resource.reference, err);
                    }
                }
                setCareTeamPractitionersMap(map);
            };
            fetchAllPractitioners();
        }
    }, [results.careTeams]);

    const handleSearchModeChange = (mode) => {
        setSearchMode(mode);

        if (mode === "circle") {
            setQuery("");
            setSearchTypes({
                providers: false,
                organizations: true,
                careTeams: true,
                attachmentRegistries: true,
                communications: true
            });
        }

        if (mode === "provider") {
            setSearchTypes(DEFAULT_SEARCH_TYPES);
        }
    };

    const handleTypeChange = (type) => {
        setSearchTypes(prev => ({ ...prev, [type]: !prev[type] }));
    };

    function flattenCareTeamPractitioners(careTeams) {
        if (!Array.isArray(careTeams)) return [];

        return careTeams.flatMap(team =>
            team.participant
                ?.filter(p => p.member?.reference?.startsWith("Practitioner/"))
                ?.map(p => ({
                    ...p.member,
                    role: p.role,
                    onBehalfOf: p.onBehalfOf,
                    careTeamId: team.id,
                    careTeamName: team.name,
                    careTeamResource: team
                })) || []
        );
    }

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setResults({
            providers: [],
            organizations: [],
            careTeams: [],
            attachmentRegistries: [],
            communications: []
        });

        try {
            const newResults = {
                providers: [],
                organizations: [],
                careTeams: [],
                attachmentRegistries: [],
                communications: []
            };

            if (searchMode === "provider" && searchTypes.providers) {
                newResults.providers = await searchProvidersByTerm(query, fhirClient);
            } else if (searchMode === "circle" && patientId) {
                if (searchTypes.organizations) newResults.organizations = await fetchOrganizationsPractitioners(patientId, fhirClient, store);
                if (searchTypes.careTeams) {
                    const careTeams = await getCareTeamsForPatient(patientId, fhirClient);
                    newResults.careTeams = flattenCareTeamPractitioners(careTeams);
                }
                if (searchTypes.attachmentRegistries) newResults.attachmentRegistries = await fetchRelatedPersonPractitioners(patientId, fhirClient);
                if (searchTypes.communications) newResults.communications = await fetchCommunicationSenders(patientId, fhirClient, store);
            }

            setResults(newResults);

            const keysWithResults = Object.keys(newResults).filter(cat =>
                (searchMode === "provider" ? cat === "providers" : cat !== "providers" && searchTypes[cat])
                && Array.isArray(newResults[cat]) && newResults[cat].length > 0
            );
            setActiveAccordionKeys(keysWithResults);
        } catch (err) {
            console.error("Error searching resources:", err);
            setError("Failed to search resources.");
        }
        setLoading(false);
    };

    const handleResultCheckbox = (practitioner, checked) => {
        const exists = recipients.some(r => r.id === practitioner.id && r.resourceType === practitioner.resourceType);
        if (checked && !exists) onRecipientsChange([...recipients, practitioner]);
        if (!checked && exists) onRecipientsChange(recipients.filter(r => !(r.id === practitioner.id && r.resourceType === practitioner.resourceType)));

        //cache any 
        try {
            fetchProvider(practitioner.id, fhirClient, store);
        } catch (err) {
            console.error("Failed to fetch practitioner:", practitioner, err);
        }
    };

    const activeCategories = Object.keys(searchTypes).filter(cat =>
        searchMode === "provider" ? cat === "providers" : cat !== "providers" && searchTypes[cat]
    );

    
    const handleCareTeamInfoClick = async (resource) => {
        const careTeamId = resource.careTeamId;
        if (!careTeamId || !patientId) {
            setErrorModalMessage("Missing CareTeam ID or patient ID.");
            setErrorModalShow(true);
            return;
        }
        try {
            const careTeam = await getCareTeam(careTeamId, fhirClient);
            if (careTeam) {
                const practitioner = {
                    reference: resource.reference,
                    display: resource.display,
                    role: resource.role,
                };
                setFhirViewerResource({ ...careTeam, selectedPractitioner: practitioner });
                setShowFhirViewerModal(true);
            } else {
                setErrorModalMessage("No CareTeam found for this ID.");
                setErrorModalShow(true);
            }
        } catch (err) {
            console.error("❌ Error fetching CareTeam:", err);
            setErrorModalMessage("Failed to fetch CareTeam details.");
            setErrorModalShow(true);
        }
    };

    return (
        <div>
            <Form className="mb-2">
                <Form.Check
                    type="radio"
                    id="search-mode-provider"
                    label="Provider"
                    name="searchMode"
                    value="provider"
                    checked={searchMode === "provider"}
                    onChange={() => handleSearchModeChange("provider")}
                    inline
                />
                <OverlayTrigger animation={false} placement="bottom" overlay={tooltipRegistry.circleOfCare}>
                    <Form.Check
                        type="radio"
                        id="search-mode-circle"
                        label="Circle of Care"
                        name="searchMode"
                        value="circle"
                        checked={searchMode === "circle"}
                        onChange={() => handleSearchModeChange("circle")}
                        inline
                    />
                </OverlayTrigger>
            </Form>

            {searchMode === "provider" && (
                <Form onSubmit={handleSearch} className="mb-2">
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder={patientId ? "Search Providers" : "Search providers by name..."}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            disabled={loading}
                        />
                        <Button type="submit" variant="primary" disabled={loading}><FaSearch /></Button>
                    </InputGroup>
                </Form>
            )}

            {searchMode === "circle" && (
                <Form onSubmit={handleSearch} className="mb-2">
                    <Row className="mt-3">
                        <Col xs={12}>
                            {["organizations", "careTeams", "attachmentRegistries", "communications"].map(type => (
                                <Form.Check
                                    key={type}
                                    inline
                                    label={type.charAt(0).toUpperCase() + type.slice(1)}
                                    type="checkbox"
                                    checked={searchTypes[type]}
                                    onChange={() => handleTypeChange(type)}
                                />
                            ))}
                        </Col>
                        <Button type="submit" variant="primary" disabled={loading}>SEARCH</Button>
                    </Row>
                </Form>
            )}

            {loading && <Spinner animation="border" size="sm" />}
            {error && <div className="text-danger">{error}</div>}

            <Accordion
                activeKey={activeAccordionKeys}
                onSelect={(key) => {
                    if (!key) return;
                    setActiveAccordionKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
                }}
            >
                {activeCategories.map(cat => (
                    <Accordion.Item eventKey={cat} key={cat}>
                        <Accordion.Header>
                            {CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                            <span className="text-muted">&nbsp;({(results[cat] || []).length})</span>
                        </Accordion.Header>
                        <Accordion.Body>
                            <ListGroup>
                                {(results[cat] || []).length === 0 && <ListGroup.Item className="text-muted">No results</ListGroup.Item>}
                                {(results[cat] || []).map(resource => {

                                    const keyId = cat === "careTeams" ? `${resource.reference}-${resource.careTeamId}` : `${resource.id}-${resource.resourceType}`;
                                    if (cat === "careTeams") {
                                        const practitioner = careTeamPractitionersMap[keyId];

                                        // 🧹 Practitioner was removed (FHIR 410 Gone)
                                        if (practitioner?.error === 410) {
                                            return (
                                                <ListGroup.Item
                                                    key={keyId}
                                                    className="text-muted"
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        backgroundColor: "#f8f9fa",
                                                        cursor: "not-allowed"
                                                    }}
                                                >
                                                    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                                        {/* Disabled checkbox placeholder for alignment */}
                                                        <Form.Check
                                                            type="checkbox"
                                                            className="me-2"
                                                            disabled
                                                            checked={false}
                                                        />

                                                        <span style={{ flex: 1, fontStyle: "italic" }}>
                                                            Practitioner Removed
                                                        </span>
                                                    </div>

                                                    {resource.careTeamName && (
                                                        <div
                                                            style={{
                                                                fontSize: "0.8em",
                                                                color: "#6c757d",
                                                                marginLeft: "26px"
                                                            }}
                                                        >
                                                            {resource.careTeamName && (
                                                                <div style={{ fontSize: "0.85em", color: "#6c757d" }}>
                                                                    [{resource.careTeamName}]
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </ListGroup.Item>
                                            );
                                        }

                                        // ⏳ Still loading
                                        if (!practitioner) {
                                            return (
                                                <ListGroup.Item key={keyId} className="text-muted">
                                                    Loading {resource.display || "practitioner"}...
                                                </ListGroup.Item>
                                            );
                                        }

                                        // ✅ Normal practitioner rendering
                                        return (
                                            <ListGroup.Item
                                                key={keyId}
                                                action
                                                style={{ display: "flex", flexDirection: "column", cursor: "pointer" }}
                                                onClick={e => {
                                                    if (
                                                        !e.target.closest(".provider-info-icon") &&
                                                        e.target.type !== "checkbox"
                                                    ) {
                                                        setSelectedForView(practitioner);
                                                    }
                                                }}
                                            >
                                                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        className="me-2"
                                                        checked={recipients.some(
                                                            r => r.id === practitioner.id && r.resourceType === practitioner.resourceType
                                                        )}
                                                        onChange={(e) =>
                                                            handleResultCheckbox(practitioner, e.target.checked)
                                                        }
                                                        onClick={e => e.stopPropagation()}
                                                    />

                                                    <span style={{ flex: 1, fontWeight: 500 }}>
                                                        {practitioner.name?.[0]?.text ||
                                                            [
                                                                practitioner.name?.[0]?.given?.join(" "),
                                                                practitioner.name?.[0]?.family
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" ")}
                                                    </span>

                                                    <img
                                                        src={CareTeamIcon}
                                                        className="ms-2 text-primary provider-info-icon"
                                                        style={{ cursor: "pointer", height: "24px", width: "24px" }}
                                                        title="Show CarePlan"
                                                        onClick={async e => {
                                                            e.stopPropagation();
                                                            await handleCareTeamInfoClick(resource);
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ fontSize: "0.85em", color: "#6c757d", lineHeight: 1.2 }}>
                                                    {practitioner.id && (
                                                        <div style={{ fontSize: "0.8em", color: "#6c757d", marginBottom: 4 }}>
                                                            {practitioner.id}
                                                        </div>
                                                    )}

                                                    {resource.careTeamName && (
                                                        <div style={{ fontSize: "0.85em", color: "#6c757d" }}>
                                                            [{resource.careTeamName}]
                                                        </div>
                                                    )}
                                                    {practitioner.address?.length > 0 && (
                                                        <div>
                                                            {practitioner.address[0].line?.join(", ")},
                                                            {practitioner.address[0].city},
                                                            {practitioner.address[0].state}
                                                        </div>
                                                    )}
                                                </div>
                                            </ListGroup.Item>
                                        );
                                    }
                                    else {

                                        const practitionerIcon = (() => {
                                            if (resource.resourceType !== "Practitioner") return PractitionerIconMale;

                                            switch (resource.gender) {
                                                case "female":
                                                    return PractitionerIconFemale;
                                                case "male":
                                                    return PractitionerIconMale;
                                                default:
                                                    return PractitionerIconMale; // or PractitionerIconUnknown
                                            }
                                        })();

                                        return (
                                            <ListGroup.Item key={keyId} action style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                                                onClick={e => { if (e.target.type !== "checkbox") setSelectedForView(resource); }}
                                            >
                                                <Form.Check
                                                    type="checkbox"
                                                    className="me-2"
                                                    checked={recipients.some(r => r.id === resource.id && r.resourceType === resource.resourceType)}
                                                    onChange={(e) => handleResultCheckbox(resource, e.target.checked)}
                                                    onClick={e => e.stopPropagation()}
                                                />


                                                <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                                    <span style={{ fontWeight: 500 }}>{resolveName(resource)}</span>
                                                    {resource.resourceType === "Practitioner" && resource.id && (
                                                        <span style={{ fontSize: "0.85em", color: "#6c757d" }}>
                                                            {resource.id}
                                                        </span>
                                                    )}
                                                    {resource.address && resource.address.length > 0 && (
                                                        <span style={{ fontSize: "0.85em", color: "#6c757d" }}>
                                                            {resource.address[0].line?.join(", ")}, {resource.address[0].city}, {resource.address[0].state}
                                                        </span>
                                                    )}
                                                </div>

                                                <img
                                                    src={practitionerIcon}
                                                    className="ms-2 text-primary provider-info-icon"
                                                    style={{ cursor: "pointer", height: "24px", width: "24px" }}
                                                    title="Show CarePlan"
                                                    
                                                    onClick={async e => {
                                                        e.stopPropagation();
                                                        setSelectedForView(resource);
                                                        setFhirViewerResource(resource);
                                                        setShowFhirViewerModal(true);
                                                    }}        
                                                />

                                            </ListGroup.Item>
                                        );
                                    }
                                })}
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>

            <div className="mt-3">
                <ProviderCard
                    title="Selected Provider"
                    providerData={selectedForView || recipients.find(r => r.resourceType === "Practitioner") || null}
                    onSelectProvider={() => { }}
                    showSearch={false}
                    fhirClient={fhirClient}
                />
            </div>

            <ResourceViewerModal
                show={showFhirViewerModal}
                onClose={() => setShowFhirViewerModal(false)}
                resource={fhirViewerResource}
                fhirClient={fhirClient}
            />

            <Modal show={errorModalShow} onHide={() => setErrorModalShow(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Provider Search Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{errorModalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setErrorModalShow(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default ProviderSearch;
